import { Button, Icon } from '@blueprintjs/core';
import React, { useCallback, useEffect, useReducer, useRef } from 'react';
import {
  DataSyncItem,
  FilterConfig,
  MappingMode,
  OptimizelyProduct,
  SyncStrategy,
  Target
} from '../../../types/DataSyncItem';
import DataSyncCgObjectsStore from '../../../stores/object/DataSyncCgObjectsStore';
import { getSourceProductInstanceId, isSourceFilled } from '../../../lib/dataSync';
import { DataSyncViewModelConverter } from '../../../utils/dataSyncViewModelConverter';
import styles from './DataSyncForm.module.sass';
import {
  FieldMappingsTableSkeleton,
  TreeFieldMappingsTable,
  useFieldMappingHeaderInfo,
} from './FormSections/TreeFieldMappingsTable';
import DataSyncFormSource from './FormSections/DataSyncFormSource';
import DataSyncFormDestination from './FormSections/DataSyncFormDestination';
import { IconNames } from '@blueprintjs/icons';
import DataSyncScheduleSection from './FormSections/DataSyncScheduleSection';
import DataSyncConfigSection from './FormSections/DataSyncConfigSection';
import Notification from './Notification';
import MainContainer from '../MainContainer';
import LoadingOverlay from '../../../components/LoadingOverlay';
import { DataSyncMappingService } from '../../../services/DataSync/DataSyncMappingService';
import { useDataSyncSave } from '../../../hooks/useDataSyncSave';
import { useGetStrategy } from '../../../hooks/useDataSyncStrategy';
import { useDataSyncObjectStore } from '../../../hooks/useDataSyncStore';
import { useFetchStores } from '../../../hooks/useFetchStore';
import { FieldDefinition } from '../../../types/FieldDefinition';
import { useValidation } from '../../../hooks/useDataSyncFormValidator';
import { filterSystemFields } from '../../../lib/dataSync';
import { useDataSyncActions } from '../../../hooks/useDataSyncActions';
import { formReducer, FormActionType } from './formReducer';

export interface DataSyncCreateProps {
  mode: 'create' | 'edit';
  onSubmit: (syncObj: DataSyncItem) => Promise<void>;
  onCancel: () => void;
  initialValues: DataSyncItem;
}

const DataSyncForm = ({mode, onSubmit, onCancel, initialValues}: DataSyncCreateProps) => {
  const [state, dispatch] = useReducer(formReducer, initialValues, (init) => ({
    viewModel: DataSyncViewModelConverter.fromBackend(init),
    sourceFields: [],
    destinationFields: [],
    destinationFieldsLoading: false,
    fieldMappingMode: mode === 'create' ? MappingMode.EDITABLE : undefined,
  }));
  const { viewModel, sourceFields, destinationFields, destinationFieldsLoading, fieldMappingMode } = state;

  const getStrategy = useGetStrategy(viewModel);

  const sourceStore = useDataSyncObjectStore(Target.SOURCE, viewModel?.source);
  const destinationStore = useDataSyncObjectStore(Target.DESTINATION, viewModel?.destination);

  // Must run before useFetchStores' effect so the first Graph destination fetch carries the
  // request context.
  const sourceProduct = viewModel?.source?.product;
  const sourceProductInstanceId = getSourceProductInstanceId(viewModel?.source);
  const sourceFilled = isSourceFilled(viewModel?.source);
  useEffect(() => {
    if (!(destinationStore instanceof DataSyncCgObjectsStore)) {
      return;
    }
    destinationStore.clearDestinationCache();
    if (mode === 'edit' && initialValues.id) {
      destinationStore.setRequestContext({ dataSyncId: initialValues.id });
      // Seed so source-scoped calls work before the picker's first fetch.
      destinationStore.setResolvedGraphSourceId(
        initialValues.destination?.config?.content_graph?.graph_source_id
      );
    } else if (sourceFilled && sourceProduct) {
      destinationStore.setRequestContext({
        sourceContext: {
          sourceProduct: sourceProduct as OptimizelyProduct,
          // HUB (OCP Database) has no instance id.
          sourceProductInstanceId: sourceProductInstanceId || '',
        },
      });
    } else {
      destinationStore.setRequestContext({});
    }
    destinationStore.fetch();
  }, [destinationStore, mode, initialValues.id, sourceProduct, sourceProductInstanceId, sourceFilled]);

  const {loaded: storesLoaded} = useFetchStores(sourceStore, destinationStore);

  const {
    refs: { configRef, sourceRef, destinationRef, mappingsRef, scheduleRef },
    isValid,
  } = useValidation(viewModel, getStrategy);

  const showScheduleSection = getStrategy() === SyncStrategy.SCHEDULED;

  // Calculate source and destination header info for field mapping table
  const { sourceInfo, destinationInfo } = useFieldMappingHeaderInfo(
    viewModel.source,
    viewModel.destination,
    sourceStore,
    destinationStore,
    storesLoaded
  );

  const { handleSave, loading, loadingMessage } = useDataSyncSave(
    initialValues,
    viewModel,
    destinationStore,
    onSubmit,
    isValid,
    sourceFields,
    destinationFields
  );

  // One-time initialization when stores finish loading.
  // Fetches source/destination fields in parallel, determines mapping mode
  // for edit mode, and generates inherited schema mappings if applicable.
  // Guarded by ref to ensure it runs exactly once.
  const initialFetchDone = useRef(false);
  useEffect(() => {
    if (!storesLoaded || initialFetchDone.current) {
      return;
    }
    initialFetchDone.current = true;

    const init = async () => {
      // Fetch source and destination fields in parallel
      // Stores are guaranteed non-null here because useFetchStores only
      // reports loaded=true after real fetches complete (not for null stores).
      const [srcFields, destFields] = await Promise.all([
        sourceStore!.getSourceObjectFields(viewModel.source.object.object_name)
          .then(filterSystemFields).catch((): FieldDefinition[] => []),
        destinationStore!.getDestinationObjectFields(viewModel.destination.object.object_name)
          .then(filterSystemFields).catch((): FieldDefinition[] => []),
      ]);

      dispatch({ type: FormActionType.SET_SOURCE_FIELDS, sourceFields: srcFields });
      dispatch({ type: FormActionType.SET_DESTINATION_FIELDS, destinationFields: destFields });

      // In create mode, fields are all we need -- mapping mode is already EDITABLE
      if (mode !== 'edit') {
        return;
      }

      // Determine whether destination inherits from source (FIXED vs EDITABLE)
      const resolvedMode = await DataSyncMappingService.determineInitialMappingMode(
        initialValues, sourceStore, destinationStore,
      );
      dispatch({ type: FormActionType.SET_MAPPING_MODE, mode: resolvedMode });

      // For inherited (FIXED) schemas, generate the full mapping set so the
      // field mapping table reflects the current source schema on load
      if (
        resolvedMode === MappingMode.FIXED &&
        !viewModel.isNew &&
        srcFields.length > 0 &&
        destFields.length > 0
      ) {
        const { mappings, updatedFields } =
          await DataSyncMappingService.generateMappingsForInheritedSchema(
            viewModel.source,
            viewModel.destination,
            sourceStore,
            destinationStore,
            viewModel.fieldMappings,
          );
        dispatch({
          type: FormActionType.INHERITED_SCHEMA_SYNCED,
          mappings: mappings.sort((a, b) => a.sourceField.localeCompare(b.sourceField)),
          destinationFields: filterSystemFields(updatedFields),
        });
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time init guarded by ref
  }, [storesLoaded]);

  const { onSourceChange, onDestinationChange, inheritSourceSchema } =
    useDataSyncActions(viewModel, destinationFields, fieldMappingMode, dispatch, sourceStore, destinationStore);

  const actionButtons = (
    <>
      <Button text="Cancel" onClick={onCancel} />
      <Button
        text="Save"
        intent="primary"
        onClick={handleSave}
        disabled={loading}
      />
    </>
  );

  const handleAutoMap = useCallback((): void => {
    const mappings = DataSyncMappingService.autoMap(
      destinationFields, sourceFields, viewModel.fieldMappings
    );
    dispatch({ type: FormActionType.SET_FIELD_MAPPINGS, mappings });
  }, [destinationFields, sourceFields, viewModel.fieldMappings]);

  return (
    <MainContainer
      title="To create a new sync, define a source and destination. Then setup field mapping"
      headerActions={actionButtons}
      scrollableContent={true}
    >
      <div className={styles.container}>
        <LoadingOverlay isOpen={loading} message={loadingMessage} />
        <DataSyncConfigSection
          value={{ name: viewModel.name }}
          ref={configRef}
          onChange={(obj) => {
            dispatch({ type: FormActionType.SET_NAME, name: obj.name });
          }}
        />

        <div className={styles.row}>
          <DataSyncFormSource
            value={viewModel.source}
            ref={sourceRef}
            dataSyncObjectsStore={sourceStore}
            onChange={onSourceChange}
            mode={mode}
            filterConfig={viewModel.filterConfig}
            onFilterConfigChange={(filterConfig: FilterConfig | undefined) => {
              dispatch({ type: FormActionType.SET_FILTER_CONFIG, filterConfig });
            }}
          />
          <div className={styles.arrowRight}>
            <Icon icon={IconNames.ARROW_RIGHT} />
          </div>

          <DataSyncFormDestination
            value={viewModel.destination}
            ref={destinationRef}
            dataSyncObjectsStore={destinationStore}
            onChange={onDestinationChange}
            inheritSourceSchema={inheritSourceSchema}
            source={viewModel.source}
            dataSyncId={mode === 'edit' ? initialValues.id : undefined}
            mode={mode}
          />
        </div>

        <div className={styles.row}>
          {fieldMappingMode === undefined || destinationFieldsLoading ? (
            <FieldMappingsTableSkeleton />
          ) : mode === 'edit' &&
            sourceFields.length === 0 &&
            destinationFields.length === 0 ? (
            <Notification
              show={true}
              type="warning"
              message="No fields are available for the source or destination. The schema may have failed to load -- please refresh the page or verify the source and destination configuration."
            />
          ) : (
            <TreeFieldMappingsTable
              ref={mappingsRef}
              viewModelFieldMappings={viewModel.fieldMappings}
              sourceFields={sourceFields}
              destinationFields={destinationFields}
              fieldMappingMode={fieldMappingMode}
              sourceInfo={sourceInfo}
              destinationInfo={destinationInfo}
              onAutoMap={handleAutoMap}
              onChange={(newFieldMappings) => {
                dispatch({ type: FormActionType.SET_FIELD_MAPPINGS, mappings: newFieldMappings });
              }}
            />
          )}
        </div>

        {showScheduleSection && (
          <div className={styles.row}>
            <DataSyncScheduleSection
              value={viewModel.interval}
              ref={scheduleRef}
              onChange={(value: string) => {
                dispatch({ type: FormActionType.SET_INTERVAL, interval: value });
              }}
            />
          </div>
        )}
      </div>
    </MainContainer>
  );
};

export default DataSyncForm;
